import { useEffect, useMemo, useState } from "react";
import { Database, Download, ExternalLink, Info, X } from "lucide-react";
import TimeSeriesChart from "./TimeSeriesChart";
import type { EventMarker, IndicatorSeries, RangeKey, ViewMode } from "../types";
import { filterByRange, mergeSeries, summarize, transformSeries, unitLabel } from "../utils/series";

interface Props {
  open: boolean;
  title: string;
  description: string;
  series: IndicatorSeries[];
  events?: EventMarker[];
  initialRange?: RangeKey;
  referenceZero?: boolean;
  onClose: () => void;
}

const ranges: RangeKey[] = ["1Y", "3Y", "5Y", "10Y", "MAX"];
const modes: { key: ViewMode; label: string }[] = [
  { key: "level", label: "수준" },
  { key: "change_1m", label: "1개월 변화" },
  { key: "change_3m", label: "3개월 변화" },
  { key: "yoy", label: "전년동월 변화" },
  { key: "zscore", label: "Z점수" },
  { key: "percentile", label: "백분위" }
];

function f(v: number | null) {
  return v == null ? "N/A" : Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2);
}

function csvCell(value: unknown) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadSeriesCsv(filename: string, series: IndicatorSeries[]) {
  const rows = mergeSeries(series);
  const header = ["date", ...series.map((s) => `${s.name} [${s.id}]`)];
  const lines = [header.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push([row.date, ...series.map((s) => row[s.id])].map(csvCell).join(","));
  }
  const blob = new Blob(["\uFEFF", lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename.replace(/[^0-9A-Za-z가-힣._-]+/g, "_");
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export default function DetailPanel({
  open,
  title,
  description,
  series,
  events = [],
  initialRange = "3Y",
  referenceZero = false,
  onClose
}: Props) {
  const [range, setRange] = useState<RangeKey>(initialRange);
  const [mode, setMode] = useState<ViewMode>("level");
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => Object.fromEntries(series.map((s) => [s.id, true])));
  const [showData, setShowData] = useState(false);


  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const visible = series.filter((s) => enabled[s.id] !== false);
  const transformed = useMemo(() => visible.map((s) => filterByRange(transformSeries(s, mode), range)), [visible, range, mode]);

  if (!open) return null;

  return (
    <div className="detail-backdrop" role="dialog" aria-modal="true" aria-label={`${title} 상세 분석`}>
      <div className="detail-panel">
        <header className="detail-header">
          <div>
            <p className="kicker">상세 시계열 분석</p>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
          <button className="icon-button icon-button--large" onClick={onClose} aria-label="닫기"><X size={20}/></button>
        </header>

        <div className="detail-toolbar">
          <div className="toolbar-group">
            <span>기간</span>
            <div className="segmented">
              {ranges.map((r) => <button key={r} className={range === r ? "is-active" : ""} onClick={() => setRange(r)}>{r}</button>)}
            </div>
          </div>
          <div className="toolbar-group">
            <span>표현</span>
            <div className="segmented segmented--scroll">
              {modes.map((m) => <button key={m.key} className={mode === m.key ? "is-active" : ""} onClick={() => setMode(m.key)}>{m.label}</button>)}
            </div>
          </div>
        </div>

        <div className="series-toggle-row">
          {series.map((s) => (
            <button key={s.id} className={`series-toggle ${enabled[s.id] !== false ? "is-active" : ""}`} onClick={() => setEnabled((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}>
              <span className="series-toggle__dot" />{s.shortName}
            </button>
          ))}
        </div>

        <TimeSeriesChart
          title={`${title} · ${modes.find((m) => m.key === mode)?.label}`}
          subtitle="동일 날짜 crosshair를 사용합니다. 결측치는 연결하지 않습니다."
          series={visible}
          range={range}
          viewMode={mode}
          events={events}
          referenceZero={referenceZero || mode === "zscore"}
          height={420}
          syncId="detail"
          showOpenButton={false}
          interactiveZoom
        />

        <div className="detail-stat-grid">
          {transformed.map((s) => {
            const st = summarize(s);
            return (
              <article className="detail-stat" key={s.id}>
                <div className="detail-stat__name">{s.shortName}</div>
                <div className="detail-stat__latest">{f(st.latest)} <small>{unitLabel(s.unit)}</small></div>
                <div className="detail-stat__rows">
                  <span>범위 최저 <b>{f(st.min)}</b></span>
                  <span>범위 평균 <b>{f(st.mean)}</b></span>
                  <span>범위 최고 <b>{f(st.max)}</b></span>
                </div>
              </article>
            );
          })}
        </div>

        <section className="data-panel">
          <button className="data-panel__toggle" onClick={() => setShowData((v) => !v)}>
            <Database size={16}/> 데이터·계산 근거 {showData ? "접기" : "보기"}
          </button>
          {showData && (
            <div className="data-table-wrap">
              <div className="data-export-row">
                <button className="secondary-button" onClick={() => downloadSeriesCsv(`${title}_raw.csv`, series)}><Download size={14}/> 원계열 CSV</button>
                <button className="secondary-button" onClick={() => downloadSeriesCsv(`${title}_${mode}_${range}.csv`, transformed)}><Download size={14}/> 현재 보기 CSV</button>
                <span>원계열 CSV는 변환 전 공식/계산계열, 현재 보기 CSV는 선택한 기간·표현 모드를 내보냅니다.</span>
              </div>
              <table className="data-table">
                <thead><tr><th>계열</th><th>기관/종류</th><th>ECOS ID</th><th>주기</th><th>기준/수집일</th><th>가공</th><th>원자료</th></tr></thead>
                <tbody>
                  {series.map((s) => (
                    <tr key={s.id}>
                      <td><strong>{s.name}</strong><br/><small>{s.id}</small></td>
                      <td>{s.source.provider}<br/><small>{s.source.quality.toUpperCase()}</small></td>
                      <td>{s.source.statCode ? `${s.source.statCode}/${s.source.itemCode ?? "?"}` : "—"}<br/><small>{s.source.itemName ?? ""}</small></td>
                      <td>{s.source.frequency}<br/><small>{s.source.nativeUnit ?? unitLabel(s.unit)}</small></td>
                      <td>{s.source.observationDate ?? "N/A"}<br/><small>{s.source.collectedAt ? new Date(s.source.collectedAt).toLocaleString("ko-KR") : "N/A"}</small></td>
                      <td>{s.source.sourceIds?.length ? `계산: ${s.source.sourceIds.join(" - ")}` : "원계열"}<br/><small>{s.source.note ?? ""}</small></td>
                      <td>
                        {s.source.url ? <a href={s.source.url} target="_blank" rel="noreferrer">열기 <ExternalLink size={12}/></a> : <span>N/A</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="data-footnote"><Info size={14}/> 공식 원자료가 없거나 검증에 실패한 계열은 N/A로 유지합니다. 계산계열은 sourceIds와 정렬 규칙을 함께 노출하며, 값이 없다고 임의 보간하지 않습니다.</div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
